/**
 * floorPlan.ts — per-floor floor-plan IMAGE upload for the native grid tool.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Stage 1 of the grid-first intake: staff upload a floor-plan image per
 *  floor; that image is the layer the boundary tracer + grid overlay sit on.
 *
 *  STORAGE PATH (new, additive):
 *    clients/{clientId}/floorplans/{floorId}/{safeName}
 *  This is a NEW path — it does NOT match the existing storage.rules patterns
 *  (`clients/{cid}/projects/{pid}/...` Sprint-A drawings). A new additive rule
 *  block is added to firebase-functions/storage.rules:
 *    match /clients/{clientId}/floorplans/{floorId}/{fileName} {
 *      allow read:           if request.auth != null;
 *      allow create, update: if request.auth != null && size<25MB && image/*;
 *      allow delete:         if request.auth != null;
 *    }
 *  ⚠ The rule is committed but deploying it is OWNER-GATED. Until it deploys,
 *  uploads to this exact path will 403 on live/preview storage (the default
 *  rule denies). The grid math, tracing UI, and Firestore writes all work
 *  WITHOUT the upload (a boundary can be traced on a placeholder), but a real
 *  floor-plan image needs the rule live.
 *
 *  WHY a new path (not reuse projects/{pid}/artefacts/): the floor-plan +
 *  boundary live on the CLIENT intake (clients/{id}/intake/L2B_R1), keyed by
 *  the intake floorId — not by a project artefact id. Keeping the image keyed
 *  by clientId+floorId means the tracer always finds the right plan for the
 *  floor it is editing, and re-uploading a floor's plan overwrites cleanly.
 *
 *  PURE/IMPURE split: path + name + type helpers are PURE + unit-tested;
 *  `uploadFloorPlan` (the only Firebase-touching fn) sits at the bottom.
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ════════════════════ pure: accepted types + size ════════════════════ */

/** Floor plans are images only (the tracer draws over a raster). 25MB cap. */
export const MAX_FLOORPLAN_BYTES = 25 * 1024 * 1024;

/** PURE — is this an image MIME the floor-plan rule accepts? Used to decide
 *  which sources are DIRECTLY traceable rasters (a PDF is NOT — it must be
 *  rasterised first). The "Choose from project files" picker uses this to keep
 *  only image sources; the upload path uses `isFloorPlanUploadType` (which also
 *  accepts a PDF, rasterised at edit time). */
export function isFloorPlanType(contentType: string): boolean {
  return (contentType || '').toLowerCase().startsWith('image/');
}

/** PURE — is this a PDF? (owner's plans are often "Adobe Scan …pdf".) A PDF can
 *  be UPLOADED as a floor plan but is rasterised (page 1) in the crop editor
 *  before tracing — it is never traced as a raw raster. */
export function isPdfFloorPlanType(contentType: string): boolean {
  return (contentType || '').toLowerCase() === 'application/pdf';
}

/** PURE — is this a type the floor-plan UPLOAD accepts? Image OR PDF. (PDFs are
 *  rasterised by the crop editor before tracing; images go straight through.) */
export function isFloorPlanUploadType(contentType: string): boolean {
  return isFloorPlanType(contentType) || isPdfFloorPlanType(contentType);
}

/** The `accept` attribute for a floor-plan file input — images + PDF. */
export const FLOORPLAN_UPLOAD_ACCEPT = 'image/*,application/pdf';

/** PURE — human-readable size for a friendly reject message. */
export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** PURE — storage-safe file name (keeps ext, strips separators, caps length). */
export function safeFileName(raw: string): string {
  const cleaned = (raw || '')
    .replace(/[\\/]/g, '_')
    // strip ASCII control chars (U+0000-U+001F and U+007F)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return cleaned || 'floorplan';
}

/**
 * PURE — the canonical Storage path for a floor's plan image.
 *   clients/{clientId}/floorplans/{floorId}/{safeName}
 * Throws on a blank clientId/floorId (both are required to scope the object).
 */
export function floorPlanStoragePath(
  clientId: string,
  floorId: string,
  fileName: string,
): string {
  const cid = (clientId || '').trim();
  const fid = (floorId || '').trim();
  if (!cid) throw new Error('clientId is required for a floor-plan path');
  if (!fid) throw new Error('floorId is required for a floor-plan path');
  return `clients/${cid}/floorplans/${fid}/${safeFileName(fileName)}`;
}

/**
 * PURE — the canonical Storage path for the TRUE-NORTH Google-Earth screenshot
 * (16th dispatch). The owner measures the true-north bearing in Google Earth,
 * screenshots it, and uploads it for future reference / the report.
 *   clients/{clientId}/north/{safeName}
 * A NEW additive path (sibling to /floorplans/) — does NOT match existing
 * storage.rules patterns. Reuses the floor-plan rule's auth+image+25MB shape;
 * an additive rule block is committed (deploy is owner-gated, same as
 * /floorplans/). Throws on a blank clientId.
 */
export function northScreenshotStoragePath(
  clientId: string,
  fileName: string,
): string {
  const cid = (clientId || '').trim();
  if (!cid) throw new Error('clientId is required for a north-screenshot path');
  return `clients/${cid}/north/${safeFileName(fileName)}`;
}

/* ════════════════════ the floor-plan record (stored on the floor) ════════════════════ */

/**
 * The floor-plan metadata persisted on `answers.floors[i].floorplan`. The
 * boundary tracer needs `width`/`height` (the natural pixel size of the
 * uploaded image) because the traced polygon is in IMAGE-PIXEL space — the
 * same space the grid math consumes (OQ-C). `downloadURL` renders the image
 * layer; `storagePath` lets a future stage re-fetch / replace it.
 */
export interface FloorPlan {
  storagePath: string;
  downloadURL: string;
  contentType: string;
  /** natural pixel width of the uploaded image (tracer coordinate space). */
  width: number;
  /** natural pixel height. */
  height: number;
}

/**
 * The TRUE-NORTH Google-Earth screenshot metadata persisted on
 * `clients/{id}.profile.north_screenshot` (16th dispatch). A durable reference
 * — `storage_path` + `download_url` + `uploaded_at` — so it survives reload and
 * is retrievable later for the report. Additive: absence breaks nothing (the
 * north bearing value itself stays at `profile.north_rotation`). snake_case keys
 * mirror the owner-locked `site_location` contract style.
 */
export interface NorthScreenshot {
  storage_path: string;
  download_url: string;
  /** ISO timestamp string of upload (client clock — informational only). */
  uploaded_at: string;
}

/** PURE — hydrate a NorthScreenshot from a stored doc map (defensive). null if
 *  absent / has no URL+path. */
export function northScreenshotFromDoc(
  d: Record<string, unknown> | null | undefined,
): NorthScreenshot | null {
  if (!d || typeof d !== 'object') return null;
  const url = typeof d.download_url === 'string' ? d.download_url : '';
  const sp = typeof d.storage_path === 'string' ? d.storage_path : '';
  if (!url && !sp) return null;
  return {
    storage_path: sp,
    download_url: url,
    uploaded_at: typeof d.uploaded_at === 'string' ? d.uploaded_at : '',
  };
}

/** PURE — the doc shape to persist (only the bounded set). */
export function northScreenshotDocBody(
  s: NorthScreenshot,
): Record<string, unknown> {
  return {
    storage_path: s.storage_path,
    download_url: s.download_url,
    uploaded_at: s.uploaded_at,
  };
}

/**
 * A floor-plan source that is ALREADY in the project's Files (a Firebase
 * Storage artefact). The "Choose from project files" picker offers these so
 * a staff member can start a floor from a plan that is already on the project
 * instead of re-uploading. Shape = the bounded subset of a `ProjectFile`
 * (lib/projectFiles) the tracer needs — kept as its own interface so this
 * module does NOT import projectFiles (no cross-dependency).
 */
export interface FloorPlanSource {
  /** stable id (for picker keys + dedupe). */
  fileId: string;
  /** display name shown in the picker. */
  fileName: string;
  /** the renderable + size-readable image URL. */
  downloadURL: string;
  /** Storage path (so a later stage can re-fetch / replace). */
  storagePath: string;
  /** MIME — used to confirm it is an image before offering it. */
  contentType: string;
}

/** PURE — keep only the IMAGE sources (a PDF/doc can't be traced as a raster). */
export function imageFloorPlanSources(
  files: ReadonlyArray<FloorPlanSource>,
): FloorPlanSource[] {
  return (files || []).filter(
    (f) => !!f && isFloorPlanType(f.contentType) && !!f.downloadURL,
  );
}

/** PURE — hydrate a FloorPlan from a stored doc map (defensive). null if absent.
 *  Carries through the ADDITIVE crop/rotate edit fields (`original`, `edit`)
 *  VERBATIM when present, so a reloaded edited plan still knows its pristine
 *  source + recipe (the editor must re-open against the source, never the bake).
 *  A plan without those fields is a plain upload — returned unchanged. */
export function floorPlanFromDoc(
  d: Record<string, unknown> | null | undefined,
): FloorPlan | null {
  if (!d || typeof d !== 'object') return null;
  const url = typeof d.downloadURL === 'string' ? d.downloadURL : '';
  const sp = typeof d.storagePath === 'string' ? d.storagePath : '';
  if (!url && !sp) return null;
  const base: FloorPlan = {
    storagePath: sp,
    downloadURL: url,
    contentType: typeof d.contentType === 'string' ? d.contentType : '',
    width: typeof d.width === 'number' ? d.width : 0,
    height: typeof d.height === 'number' ? d.height : 0,
  };
  // Preserve the crop/rotate edit chain if this is a derived (edited) plan.
  const out = base as FloorPlan & Record<string, unknown>;
  if (d.original && typeof d.original === 'object') out.original = d.original;
  if (d.edit && typeof d.edit === 'object') out.edit = d.edit;
  return out;
}

/** PURE — the doc shape to persist (only the bounded set + the additive
 *  crop/rotate edit fields when present). */
export function floorPlanDocBody(fp: FloorPlan): Record<string, unknown> {
  const body: Record<string, unknown> = {
    storagePath: fp.storagePath,
    downloadURL: fp.downloadURL,
    contentType: fp.contentType,
    width: fp.width,
    height: fp.height,
  };
  const extra = fp as FloorPlan & Record<string, unknown>;
  if (extra.original && typeof extra.original === 'object') {
    body.original = extra.original;
  }
  if (extra.edit && typeof extra.edit === 'object') {
    body.edit = extra.edit;
  }
  return body;
}

/* ════════════════════ impure: the upload (Firebase Storage) ════════════════════ */

import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { storage } from './firebase';

/**
 * Read the natural pixel dimensions of an image File in the browser. Returns
 * {width,height} from an off-DOM Image element. Rejects on a non-decodable
 * file. (Stays in the impure section — uses the DOM Image API.)
 */
export function readImageSize(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    img.src = url;
  });
}

/**
 * Read the natural pixel dimensions of an image from a URL (a Firebase
 * Storage download URL of a file already in the project). Loads an off-DOM
 * Image — `naturalWidth`/`naturalHeight` need NO canvas read, so there is no
 * CORS taint concern (we never read pixels, only the intrinsic size). Rejects
 * on a non-loadable URL.
 */
export function readImageSizeFromUrl(
  url: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No image URL.'));
      return;
    }
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Could not load that image.'));
    img.src = url;
  });
}

/**
 * Build a FloorPlan record from a project file already in Storage (the
 * "Choose from project files" path). NO upload — it reuses the existing
 * `downloadURL`/`storagePath`, and only loads the image to read its natural
 * width/height (the tracer's pixel coordinate space). Rejects a non-image
 * source (friendly message) and an unreadable image.
 */
export async function floorPlanFromProjectFile(
  src: FloorPlanSource,
): Promise<FloorPlan> {
  if (!src || !src.downloadURL) {
    throw new Error('That file has no image URL.');
  }
  if (!isFloorPlanType(src.contentType)) {
    throw new Error(
      `"${src.fileName || 'that file'}" is not an image — pick a JPG or PNG floor plan.`,
    );
  }
  const { width, height } = await readImageSizeFromUrl(src.downloadURL);
  return {
    storagePath: src.storagePath || '',
    downloadURL: src.downloadURL,
    contentType: src.contentType,
    width,
    height,
  };
}

/**
 * Upload a floor-plan to clients/{clientId}/floorplans/{floorId}/{name} and
 * return the FloorPlan record (path + URL + natural pixel size). Accepts an
 * IMAGE (JPG/PNG/…) OR a PDF (the owner's plans are often "Adobe Scan …pdf").
 *
 * A PDF cannot be traced as a raster AND the `/floorplans/` Storage rule is
 * image-only, so a PDF is RASTERISED in the browser (the chosen page → JPEG)
 * BEFORE upload — the object that lands in Storage is always an image, identical
 * to a normal image upload downstream (crop/rotate, tracing, report). `pdfPage`
 * (1-based, default 1) selects WHICH page of a multi-page PDF to rasterise — the
 * caller passes the page the staff chose in the page picker. For a single-page
 * PDF (or an image, where it is ignored) the default page 1 is correct.
 * Validates type + size client-side (friendly reject). Throws a readable Error on
 * a rejected type / oversize / a Storage 403 (rule not yet deployed) — the caller
 * toasts it.
 */
export async function uploadFloorPlan(
  clientId: string,
  floorId: string,
  file: File,
  pdfPage = 1,
): Promise<FloorPlan> {
  if (!isFloorPlanUploadType(file.type)) {
    throw new Error(
      `"${file.type || 'unknown type'}" is not supported — upload a JPG/PNG image or a PDF of the floor plan.`,
    );
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(
      `That file is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`,
    );
  }

  // A PDF → rasterise page 1 to a JPEG (image-only Storage rule + traceable
  // raster). An image → upload as-is. Either way we land an image in Storage.
  let uploadFile = file;
  let width: number;
  let height: number;
  if (isPdfFloorPlanType(file.type)) {
    const { pdfFileToImageFile } = await import('./imageCropRotate');
    const raster = await pdfFileToImageFile(file, pdfPage);
    uploadFile = raster.file;
    width = raster.width;
    height = raster.height;
  } else {
    const dims = await readImageSize(file);
    width = dims.width;
    height = dims.height;
  }

  const contentType = uploadFile.type || 'image/jpeg';
  const storagePath = floorPlanStoragePath(clientId, floorId, uploadFile.name);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, uploadFile, { contentType });
  const downloadURL = await getDownloadURL(sRef);
  return {
    storagePath,
    downloadURL,
    contentType,
    width,
    height,
  };
}

/**
 * Upload an ALREADY-BAKED (cropped + rotated) derived floor-plan File to the
 * SAME `clients/{cid}/floorplans/{floorId}/` path (a `derived-…` filename, so it
 * lives beside — never overwrites — the original upload). The baker
 * (`imageCropRotate.bakeImage`) has already produced the File + its natural
 * width/height, so we do NOT re-read the size here (a baked canvas has no
 * EXIF/orientation surprises). Returns a FloorPlan record pointing at the
 * derived image — the caller layers `original` + `edit` onto it. Reuses the
 * floor-plan storage rule (image-only, 25MB, auth-gated) verbatim — NO rules
 * change. Throws a readable Error on oversize / a Storage 403.
 */
export async function uploadDerivedFloorPlan(
  clientId: string,
  floorId: string,
  file: File,
  width: number,
  height: number,
): Promise<FloorPlan> {
  if (!isFloorPlanType(file.type)) {
    throw new Error('The edited image is not a recognised image type.');
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(
      `The edited image is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`,
    );
  }
  const storagePath = floorPlanStoragePath(clientId, floorId, file.name);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, file, { contentType: file.type || 'image/jpeg' });
  const downloadURL = await getDownloadURL(sRef);
  return {
    storagePath,
    downloadURL,
    contentType: file.type || 'image/jpeg',
    width,
    height,
  };
}

/**
 * Upload an ALREADY-BAKED (cropped + optionally rotated) north / location
 * screenshot File to `clients/{cid}/north/` (a `derived-…` filename beside the
 * original). Returns a NorthScreenshot ref (path + URL + uploaded-at). Same
 * auth+image+25MB rule as the plain north upload — NO rules change. Used to strip
 * a Google-Earth tool panel off the location screenshot (§B1 case).
 */
export async function uploadDerivedNorthScreenshot(
  clientId: string,
  file: File,
): Promise<NorthScreenshot> {
  if (!isFloorPlanType(file.type)) {
    throw new Error('The edited screenshot is not a recognised image type.');
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(
      `The edited screenshot is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`,
    );
  }
  const storagePath = northScreenshotStoragePath(clientId, file.name);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, file, { contentType: file.type || 'image/png' });
  const downloadURL = await getDownloadURL(sRef);
  return {
    storage_path: storagePath,
    download_url: downloadURL,
    uploaded_at: new Date().toISOString(),
  };
}

/**
 * Upload the TRUE-NORTH Google-Earth screenshot to
 * clients/{clientId}/north/{name} and return a durable NorthScreenshot ref
 * (path + URL + uploaded-at). Image-only, 25MB cap (same as a floor plan).
 * Unlike a floor plan we do NOT read the natural pixel size — the screenshot is
 * a reference image, not a tracing surface. Throws a readable Error on a
 * rejected type / oversize / a Storage 403 (rule not yet deployed).
 */
export async function uploadNorthScreenshot(
  clientId: string,
  file: File,
): Promise<NorthScreenshot> {
  if (!isFloorPlanType(file.type)) {
    throw new Error(
      `"${file.type || 'unknown type'}" is not an image — upload a JPG or PNG screenshot.`,
    );
  }
  if (file.size > MAX_FLOORPLAN_BYTES) {
    throw new Error(
      `That image is ${humanSize(file.size)} — the limit is ${humanSize(MAX_FLOORPLAN_BYTES)}.`,
    );
  }
  const storagePath = northScreenshotStoragePath(clientId, file.name);
  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, file, { contentType: file.type || 'image/png' });
  const downloadURL = await getDownloadURL(sRef);
  return {
    storage_path: storagePath,
    download_url: downloadURL,
    uploaded_at: new Date().toISOString(),
  };
}
